import React from "react";
import {
  Box,
  Button,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import Navigation from "./navigation";
import AppFooter from "./app-footer";
import useComponentStyles from "../hooks/useComponentStyles";
import { normalizeColor, tintOf } from "../helpers/component-styles";
import { postToPublisher } from "../utils/publisherMessage";

// Default Done handler — forwards the terminal status to the
// publisher exactly like DataContext.postMessage does, including
// the React Native bridge if present.
const defaultOnDone = (status) => {
  if (
    typeof window !== "undefined" &&
    window.ReactNativeWebView?.postMessage
  ) {
    postToPublisher(
      { status, source: "result-screen" },
      { target: "react_native", trigger: "done_button" },
    );
    return;
  }
  if (typeof window !== "undefined") {
    postToPublisher(status, { target: "parent", trigger: "done_button" });
  }
};

// Compact tinted result chip, Cash-App style. The chip pops in with
// a back-overshoot bounce, then a mark strokes through it. PASS
// uses the active Mantine primary color (so it follows the
// publisher's brand color); FAIL is always red[6] on a red[1] tint
// since "failure = red" is a near-universal signal.
//
// `styles.components.result.iconColor` / `iconBackground` override
// both on PASS and FAIL so a monochrome brand can keep the result
// icon neutral. When only iconColor is supplied the disc is a
// translucent tint of it, mirroring the primary[1]/primary[6] pair.
function AnimatedResultChip({
  status = "PASS",
  size = 48,
  iconColor,
  iconBackground,
}) {
  const theme = useMantineTheme();
  const isPass = status === "PASS";
  const isReview = status === "REVIEW";
  const primaryShades =
    theme.colors[theme.primaryColor] || theme.colors.teal;

  const customAccent = normalizeColor(iconColor);
  const customTint =
    normalizeColor(iconBackground) ||
    (customAccent ? tintOf(theme, customAccent, 0.15) : null);

  // REVIEW (result held for a human) is neither success nor failure,
  // so it gets a neutral gray chip with a clock instead of red.
  const tint =
    customTint ||
    (isPass
      ? primaryShades[1] || "rgba(0, 201, 100, 0.18)"
      : isReview
        ? theme.colors.gray[2]
        : theme.colors.red[1]);
  const accent =
    customAccent ||
    (isPass
      ? primaryShades[6] || "#00C964"
      : isReview
        ? theme.colors.gray[7]
        : theme.colors.red[6]);

  // Both marks fit inside a 100x100 viewBox at the same stroke
  // weight so the chip reads consistently across PASS / FAIL.
  // Check path total length ≈ 60.8, X paths total ≈ 64.
  const reviewMark = (
    <g>
      <circle
        cx="50"
        cy="50"
        r="26"
        fill="none"
        stroke={accent}
        strokeWidth="7"
        style={{
          strokeDasharray: 164,
          strokeDashoffset: 164,
          animation: "resultChipDrawMark 360ms ease-out 360ms forwards",
        }}
      />
      <path
        d="M50 36 L50 51 L60 58"
        fill="none"
        stroke={accent}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 28,
          strokeDashoffset: 28,
          animation: "resultChipDrawMark 220ms ease-out 640ms forwards",
        }}
      />
    </g>
  );

  const mark = isReview ? reviewMark : isPass ? (
    <path
      d="M30 52 L45 67 L72 38"
      fill="none"
      stroke={accent}
      strokeWidth="8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        strokeDasharray: 65,
        strokeDashoffset: 65,
        animation: "resultChipDrawMark 320ms ease-out 360ms forwards",
      }}
    />
  ) : (
    <g>
      <path
        d="M34 34 L66 66"
        fill="none"
        stroke={accent}
        strokeWidth="8"
        strokeLinecap="round"
        style={{
          strokeDasharray: 46,
          strokeDashoffset: 46,
          animation:
            "resultChipDrawMark 240ms ease-out 360ms forwards",
        }}
      />
      <path
        d="M66 34 L34 66"
        fill="none"
        stroke={accent}
        strokeWidth="8"
        strokeLinecap="round"
        style={{
          strokeDasharray: 46,
          strokeDashoffset: 46,
          animation:
            "resultChipDrawMark 240ms ease-out 540ms forwards",
        }}
      />
    </g>
  );

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes resultChipPop": {
          "0%": { transform: "scale(0.4)", opacity: 0 },
          "60%": { transform: "scale(1.08)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
        "@keyframes resultChipDrawMark": {
          "0%": {},
          "100%": { strokeDashoffset: 0 },
        },
        animation:
          "resultChipPop 460ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        style={{ display: "block" }}
      >
        <circle cx="50" cy="50" r="48" fill={tint} />
        {mark}
      </svg>
    </Box>
  );
}

// Visual port of the inline PASS/FAIL result rendering currently
// living in DataContext.js (lines ~1594-1624). Pulled out as its
// own component so we can preview/iterate in Storybook without
// touching the production render path. When we're happy with the
// new visual, DataContext can swap to use this component directly.
export default function ResultScreen({
  status = "PASS",
  successTitle = "You've been verified",
  successSubTitle = "You're all set — you can safely close this window.",
  failureTitle = "We couldn't verify you",
  failureSubTitle =
    "We weren't able to complete your verification. You can safely close this window.",
  // status "REVIEW": the result is held for manual review.
  reviewTitle = "We received your submission",
  reviewSubTitle =
    "Your ID is being reviewed. You will hear from us shortly. You can safely close this window.",
  logoUrl = "",
  showNavigation = true,
  // When false, the screen renders without a Done button — used in
  // legacy flows that still auto-fire the publisher message on
  // status flip. When true (default), the parent should DELAY the
  // automatic terminal postMessage and rely on this button as the
  // explicit user ack.
  showDoneButton = true,
  doneLabel = "Done",
  onDone,
  // Optional result-icon colors (styles.components.result). Props win
  // so DataContext can pass them explicitly; otherwise fall back to
  // the session context when this screen is mounted inside it.
  iconColor,
  iconBackground,
}) {
  const isPass = status === "PASS";
  const isReview = status === "REVIEW";
  const title = isPass ? successTitle : isReview ? reviewTitle : failureTitle;
  const subTitle = isPass
    ? successSubTitle
    : isReview
      ? reviewSubTitle
      : failureSubTitle;
  const componentStyles = useComponentStyles();
  // result.iconColor wins; on a success screen the shared
  // checkmark.color is the next fallback, so one "done" color can set the
  // success mark along with every other check-mark in the journey.
  const resolvedIconColor =
    iconColor ??
    componentStyles.result?.iconColor ??
    (isPass ? componentStyles.checkmark?.color : undefined);
  const resolvedIconBackground =
    iconBackground ?? componentStyles.result?.iconBackground;
  const handleDone = () => {
    if (typeof onDone === "function") {
      onDone(status);
      return;
    }
    defaultOnDone(status);
  };

  return (
    <Box
      sx={(t) => ({
        // Fills whatever container the parent provides. On mobile /
        // narrow the parent wraps us at 100vh; on wide desktop the
        // parent wraps us inside the same 400x700 card App.js uses
        // for its journey routes so the result screen lines up with
        // the rest of the flow.
        height: "100%",
        minHeight: "100%",
        backgroundColor:
          t.colorScheme === "dark" ? t.colors.dark[7] : t.white,
        display: "flex",
        flexDirection: "column",
      })}
    >
      {showNavigation && (
        <Navigation logo_url={logoUrl} forceHideClose />
      )}

      {/* Content area — top-aligned icon/title/subtitle, flex spacer,
          then Done button pinned at the bottom of the content area.
          `minHeight: 0` lets the flex child shrink so the footer
          below always stays in view. */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "32px 24px 24px",
        }}
      >
        <Stack spacing="md" align="flex-start">
          <AnimatedResultChip
            status={status}
            size={48}
            iconColor={resolvedIconColor}
            iconBackground={resolvedIconBackground}
          />
          <Stack spacing={6}>
            <Text component="h1" m={0} fz={24} fw={700}>
              {title}
            </Text>
            <Text c="dimmed" fz="md">
              {subTitle}
            </Text>
          </Stack>
        </Stack>

        <Box style={{ flex: 1 }} />

        {showDoneButton && (
          <Button
            fullWidth
            size="lg"
            onClick={handleDone}
            styles={{ root: { color: "#fff" } }}
          >
            {doneLabel}
          </Button>
        )}
      </Box>

      {/* Same footer the journey shows on every other route so the
          screen doesn't lose the Powered By / Privacy Policy chrome
          after replacing App.js's wrapper. */}
      <AppFooter />
    </Box>
  );
}
